import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Tooltip,
  Chip,
  CircularProgress,
  Fade,
  Collapse,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';

import { styled } from '@mui/material/styles';
import { useSelector } from "react-redux";
import axios from "axios";
import { NavHashLink } from 'react-router-hash-link';
import { MdAssignment, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { FaTasks, FaClock, FaUsers } from 'react-icons/fa';
import { useAuthToken } from "../TokenContext";
import { FormattedMessage, useIntl } from 'react-intl';
const MainContainer = styled(Box)(({ theme, collapsed }) => ({
  width: collapsed ? 'calc(100vw - 80px)' : 'calc(100vw - 260px)',
  maxWidth: collapsed ? 1400 : 1200,
  margin: '0 auto',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(3),

  [theme.breakpoints.down('lg')]: {
    width: collapsed ? 'calc(100vw - 70px)' : 'calc(100vw - 250px)',
    maxWidth: collapsed ? 1200 : 1000,
    padding: theme.spacing(3),
  },

  [theme.breakpoints.down('md')]: {
    width: collapsed ? 'calc(100vw - 60px)' : 'calc(100vw - 240px)',
    maxWidth: collapsed ? 1000 : 800,
    padding: theme.spacing(2),
  },

  [theme.breakpoints.down('sm')]: {
    width: 'calc(100vw - 24px)',
    maxWidth: '100%',
    padding: theme.spacing(2),
  },

  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const PodCard = styled(Card)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(1),
  background: 'var(--artemy-bg-elevated)',
  boxShadow: 'none',
  border: '1px solid var(--artemy-border)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: 'none',
    borderColor: 'var(--artemy-primary)',
  }
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  margin: "auto",
  minHeight: '80vh',
  gap: theme.spacing(2),
  '& .MuiCircularProgress-root': {
    color: theme.palette.mode === 'dark' ? '#4FC3F7' : '#00B4D8',
  }
}));

const PodHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(79, 195, 247, 0.08)'
      : 'rgba(0, 180, 216, 0.02)',
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  }
}));

const StatusBadge = styled(Chip)(({ theme, status }) => ({
  borderRadius: theme.spacing(2),
  fontWeight: 600,
  backgroundColor: theme.palette.mode === 'dark'
    ? (status === 'Active' ? 'rgba(0, 168, 84, 0.2)' : 'rgba(255, 77, 79, 0.2)')
    : (status === 'Active' ? '#e3fcef' : '#ffedef'),
  color: theme.palette.mode === 'dark'
    ? (status === 'Active' ? '#4caf50' : '#ff6b6b')
    : (status === 'Active' ? '#00a854' : '#ff4d4f'),
}));

const ActionButton = styled(NavHashLink)(({ theme }) => ({
  backgroundColor: 'var(--artemy-bg-base)',
  color: 'var(--artemy-text-primary)',
  border: '1px solid var(--artemy-border)',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  boxShadow: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'var(--artemy-bg-elevated)',
    color: 'var(--artemy-primary)',
    borderColor: 'var(--artemy-primary)'
  },
  '&.Mui-disabled': {
    opacity: 0.5,
    pointerEvents: 'none',
  }
}));

const ActionSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: 'var(--artemy-bg-base)',
  borderTop: '1px solid var(--artemy-border)',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(5, 1fr)',
    padding: theme.spacing(3),
    gap: theme.spacing(4),
  }
}));

const ActionGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const PodIcon = styled(Box)(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(79, 195, 247, 0.2)'
    : 'rgba(0, 180, 216, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(1),
  '& svg': {
    color: theme.palette.mode === 'dark' ? '#4FC3F7' : '#00B4D8',
    fontSize: '1.2rem',
  },
  [theme.breakpoints.up('sm')]: {
    width: 40,
    height: 40,
    marginRight: theme.spacing(2),
    '& svg': {
      fontSize: '1.5rem',
    }
  }
}));

const PodTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: theme.palette.mode === 'dark' ? '#E0E0E0' : 'inherit',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.25rem',
  }
}));

const Podstalent = ({ collapsed }) => {
  const intl = useIntl();
  const token = useAuthToken();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useSelector(state => state.auth);
  const [podDetails, setPodDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPod, setExpandedPod] = useState(null);

  useEffect(() => {
    const fetchPodDetails = async () => {
      try {
        const response = await axios.post(
          process.env.REACT_APP_TALENT_PODLISTTALENT,
          { candidateID: user.data[0].c_id },
          { headers: { Authorization: token, 'Content-Type': 'application/json' } }
        );
        setPodDetails(response?.data?.data);
        setLoading(false);
      } catch (err) {
        console.error("Error:", err);
        setLoading(false);
      }
    };

    fetchPodDetails();
  }, [token, user]);

  const handlePodClick = (podId) => {
    setExpandedPod(expandedPod === podId ? null : podId);
  };

  if (loading) {
    return (
      <Fade in={true} timeout={300}>
        <LoadingContainer>
          <CircularProgress size={isMobile ? 40 : 60} />
          <Typography variant="h6" color="textSecondary">
            Loading Pods...
          </Typography>
        </LoadingContainer>
      </Fade>
    );
  }

  if (!loading && (!podDetails || podDetails.length === 0)) {
    return (
      <Fade in={true} timeout={300}>
        <LoadingContainer>
          <Typography variant="h6" color="textSecondary">
            No pods found
          </Typography>
        </LoadingContainer>
      </Fade>
    );
  }

  return (
    <MainContainer collapsed={collapsed}>
      <Box textAlign="center" mb={isMobile ? 3 : 6}>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          fontWeight={700}
          color="#2c3e50"
          gutterBottom
        >
          <FormattedMessage id="yourPods" defaultMessage="Your Pods" />
        </Typography>
        <Typography
          variant={isMobile ? "body2" : "subtitle1"}
          color="text.secondary"
        >
          <FormattedMessage id="managePods" defaultMessage="Manage all your pods and their activities" />
        </Typography>
      </Box>

      {podDetails?.map((pod) => (
        <Fade in={true} key={pod.podID}>
          <PodCard>
            <PodHeader onClick={() => handlePodClick(pod.podID)}>
              <Box display="flex" alignItems="center">
                <PodIcon>
                  <FaUsers />
                </PodIcon>
                <Box>
                  <PodTitle variant="h6" fontWeight={600}>
                    {pod.podName}
                  </PodTitle>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <StatusBadge
                  label={pod.status}
                  status={pod.status}
                  size={isMobile ? "small" : "medium"}
                />
                {expandedPod === pod.podID ?
                  <MdExpandLess size={isMobile ? 20 : 24} /> :
                  <MdExpandMore size={isMobile ? 20 : 24} />
                }
              </Box>
            </PodHeader>

            <Collapse in={expandedPod === pod.podID}>
              <Divider />
              <ActionSection>
                <ActionGroup>
                  <Tooltip title={
                    pod.status === 'Active'
                      ? intl.formatMessage({ id: 'podActivity' })
                      : intl.formatMessage({ id: 'inactivePod' })
                  } arrow>
                    <span>
                      <ActionButton
                        disabled={pod.status !== 'Active'}
                        to={{
                          pathname: '/Podactivitytalent',
                          state: { podid: pod.podID, clientid: pod.clientID }
                        }}
                      >
                        <MdAssignment />
                      </ActionButton>
                    </span>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="activity" defaultMessage="Activity" />
                  </Typography>
                </ActionGroup>

                <ActionGroup>
                  <Tooltip title={
                    pod.status === 'Active'
                      ? intl.formatMessage({ id: 'podRetro' })
                      : intl.formatMessage({ id: 'inactivePod' })
                  } arrow>
                    <span>
                      <ActionButton
                        disabled={pod.status !== 'Active'}
                        to={{
                          pathname: '/PodactivitytalentRetro',
                          state: { podid: pod.podID }
                        }}
                      >
                        <MdAssignment />
                      </ActionButton>
                    </span>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="retro" defaultMessage="Retro" />
                  </Typography>
                </ActionGroup>

                <ActionGroup>
                  <Tooltip title={
                    pod.status === 'Active'
                      ? intl.formatMessage({ id: 'podTasks' })
                      : intl.formatMessage({ id: 'inactivePod' })
                  } arrow>
                    <span>
                      <ActionButton
                        disabled={pod.status !== 'Active'}
                        to={{
                          pathname: '/Createtasktalent',
                          state: { podname: pod.podName, podid: pod.podID }
                        }}
                      >
                        <FaTasks />
                      </ActionButton>
                    </span>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="tasks" defaultMessage="Tasks" />
                  </Typography>
                </ActionGroup>

                <ActionGroup>
                  <Tooltip title={
                    pod.status === 'Active'
                      ? intl.formatMessage({ id: 'timeSubmit' })
                      : intl.formatMessage({ id: 'inactivePod' })
                  } arrow>
                    <span>
                      <ActionButton
                        disabled={pod.status !== 'Active'}
                        to={{
                          pathname: '/TimeSubmit',
                          state: { podName: pod.podName, podid: pod.podID }
                        }}
                      >
                        <FaClock />
                      </ActionButton>
                    </span>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="time" defaultMessage="Time" />
                  </Typography>
                </ActionGroup>
                <ActionGroup>
                  <Tooltip title={
                    pod.status === 'Active'
                      ? intl.formatMessage({ id: 'timeSubmit' })
                      : intl.formatMessage({ id: 'inactivePod' })
                  } arrow>
                    <span>
                      <ActionButton
                        disabled={pod.status !== 'Active'}
                        to={{
                          pathname: '/TalentTicketSearch',
                          state: { podName: pod.podName, podid: pod.podID }
                        }}
                      >
                        <FaClock />
                      </ActionButton>
                    </span>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="tickets" defaultMessage="Tickets" />
                  </Typography>
                </ActionGroup>
              </ActionSection>
            </Collapse>
          </PodCard>
        </Fade>
      ))}
    </MainContainer>
  );
};

export default Podstalent;